import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, BookOpen, MapPin, Calendar, Pencil, Sparkles, Loader2 } from 'lucide-react';
import TopBar from '@/components/TopBar';
import { useCity } from '@/context/CityContext';
import { aiChatStream } from '@/lib/api';
import { toast } from 'sonner';

const KEY = 'gt_diary_entries';

function loadEntries() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
}
function saveEntries(arr) {
  try { localStorage.setItem(KEY, JSON.stringify(arr.slice(-200))); } catch {}
}

export default function TripDiary() {
  const { city } = useCity();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [drafting, setDrafting] = useState(false);

  useEffect(() => { setEntries(loadEntries()); }, []);

  const aiDraft = () => {
    if (drafting) return;
    setDrafting(true);
    const visitedToday = entries
      .filter(e => {
        try { return new Date(e.created_at).toDateString() === new Date().toDateString(); }
        catch { return false; }
      })
      .map(e => e.city)
      .filter(Boolean);
    const place = city?.name ? `${city.name}${city.country ? ', ' + city.country : ''}` : 'a destination';
    const seed = (draft || '').trim();
    const message = (
      `Draft a short, vivid first-person trip-diary entry about today in ${place}. ` +
      (visitedToday.length ? `I also moved through these places today: ${visitedToday.join(', ')}. ` : '') +
      (seed ? `My quick notes: "${seed}". ` : '') +
      `Keep it 4-6 sentences. Reflective, warm, sensory. Plain prose, no bullet points, no quotes, no markdown.`
    );
    let acc = '';
    aiChatStream(
      { session_id: `diary-${Date.now()}`, message, provider: 'auto', model: 'auto',
        city_name: city?.name, country: city?.country, country_code: city?.country_code,
        lat: city?.lat, lon: city?.lon },
      {
        onToken: (t) => { acc += t; setDraft(acc); },
        onDone: () => { setDrafting(false); toast.success('AI draft ready · edit before saving'); },
        onError: () => { setDrafting(false); toast.error('Could not draft entry'); },
      }
    );
  };

  const grouped = useMemo(() => {
    const m = {};
    entries.slice().reverse().forEach(e => {
      const key = `${e.city || 'Somewhere'}, ${e.country || ''}`.trim().replace(/,\s*$/, '');
      (m[key] = m[key] || []).push(e);
    });
    return Object.entries(m);
  }, [entries]);

  const startNew = () => { setComposing(true); setEditingId(null); setDraft(''); };
  const startEdit = (e) => { setComposing(true); setEditingId(e.id); setDraft(e.body); };
  const cancel = () => { setComposing(false); setDraft(''); setEditingId(null); };

  const save = () => {
    const text = draft.trim();
    if (!text) return;
    if (editingId) {
      const next = entries.map(e => e.id === editingId ? { ...e, body: text, edited_at: new Date().toISOString() } : e);
      setEntries(next); saveEntries(next);
    } else {
      const entry = {
        id: `d_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
        city: city?.name, country: city?.country, country_code: city?.country_code,
        lat: city?.lat, lon: city?.lon,
        body: text,
        created_at: new Date().toISOString(),
      };
      const next = [...entries, entry];
      setEntries(next); saveEntries(next);
    }
    cancel();
  };

  const remove = (id) => {
    if (!window.confirm('Delete this entry?')) return;
    const next = entries.filter(e => e.id !== id);
    setEntries(next); saveEntries(next);
  };

  return (
    <div data-testid="diary-screen">
      <TopBar />

      <div className="px-5 pt-1 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#15291C] flex items-center justify-center shadow-[0_8px_22px_rgba(15,26,19,0.18)]">
            <BookOpen className="w-5 h-5 text-[#C4FF3D]" />
          </div>
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#15291C]">Trip diary</p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F1A13]">Your stories</h1>
          </div>
        </div>
        {!composing && (
          <button type="button" onClick={startNew}
            className="gt-btn-primary !py-2.5 !px-3.5 text-[13px] flex items-center gap-1.5"
            data-testid="diary-new">
            <Plus className="w-4 h-4" /> Note
          </button>
        )}
      </div>

      {composing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
          className="px-5 mt-4">
          <div className="glass-card-strong p-4">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold mb-2">
              {editingId ? 'Edit memory' : `Memory in ${city?.name || 'this place'}`}
            </p>
            <textarea
              value={draft} onChange={e => setDraft(e.target.value)}
              placeholder="What happened? Who did you meet? Tiny moments worth remembering…"
              rows={6}
              className="w-full bg-[#F2F0E4] border border-[#0F1A13]/10 rounded-2xl p-3 text-[14px] text-[#0F1A13] placeholder:text-[#7A8A80] outline-none focus:border-[#15291C]/20 resize-none"
              data-testid="diary-textarea"
            />
            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button" onClick={aiDraft} disabled={drafting}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-semibold bg-gradient-to-br from-[#C4FF3D] via-[#9be635] to-[#F2A47A] text-[#0F1A13] shadow-[0_6px_18px_rgba(196,255,61,0.40)] disabled:opacity-60"
                data-testid="diary-ai-draft"
              >
                {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {drafting ? 'Drafting…' : 'AI draft today'}
              </button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={cancel} className="px-4 py-2 rounded-full bg-[#0F1A13]/05 border border-[#0F1A13]/10 text-[#0F1A13] text-[12.5px] font-semibold gt-focus" data-testid="diary-cancel">Cancel</button>
                <button type="button" onClick={save} disabled={!draft.trim() || drafting} className="gt-btn-primary !py-2 !px-4 text-[13px] disabled:opacity-50" data-testid="diary-save">{editingId ? 'Update' : 'Save'}</button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="px-5 mt-5 pb-4 flex flex-col gap-5" data-testid="diary-entries">
        {grouped.length === 0 && !composing && (
          <div className="glass-card p-5 text-[#4A5A50] text-sm text-center">
            No entries yet. Tap <strong className="text-[#15291C]">Note</strong> to capture your first travel memory.
          </div>
        )}
        {grouped.map(([place, items]) => (
          <div key={place}>
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold mb-2 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> {place}
            </p>
            <div className="flex flex-col gap-2">
              {items.map(e => (
                <div key={e.id} className="glass-card p-4" data-testid={`diary-entry-${e.id}`}>
                  <p className="text-[12px] text-[#7A8A80] flex items-center gap-1.5 mb-1.5"><Calendar className="w-3 h-3" /> {new Date(e.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  <p className="text-[14px] text-[#0F1A13] whitespace-pre-wrap leading-relaxed">{e.body}</p>
                  <div className="mt-2.5 flex items-center justify-end gap-1">
                    <button type="button" onClick={() => startEdit(e)} aria-label="Edit"
                      className="w-8 h-8 rounded-full bg-[#0F1A13]/05 border border-[#0F1A13]/10 flex items-center justify-center text-[#4A5A50] hover:bg-[#0F1A13]/08 gt-focus">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => remove(e.id)} aria-label="Delete"
                      className="w-8 h-8 rounded-full bg-[#F472B6]/10 border border-[#F472B6]/25 flex items-center justify-center text-[#B91C2E] hover:bg-[#F472B6]/20 gt-focus">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
